import { useState, useEffect } from "react";
import { Camera, Lock, User, Landmark, Phone } from "lucide-react";
import Markdown from "react-markdown";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { type AffiliateRecord, updateAffiliate } from "@/lib/affiliate";
import { SOCIAL_PLATFORMS } from "@/lib/social-utils";
import { useAffiliateForm, buildSocialsPayload } from "@/hooks/useAffiliateForm";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const sections = [
  { id: "general", label: "General", icon: User },
  { id: "contact", label: "Contact Details", icon: Phone },
  { id: "bank", label: "Bank Details", icon: Landmark },
] as const;

export type SectionId = (typeof sections)[number]["id"];

interface Props {
  affiliate: AffiliateRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdated: (record: AffiliateRecord) => void;
  initialSection?: SectionId;
  onSectionChange?: (id: SectionId) => void;
}

/** Strip markdown syntax to get rendered text length (e.g. [text](url) → "text") */
function renderedLength(md: string): number {
  return md
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1") // [text](url) → text
    .replace(/[*_]{1,2}([^*_]+)[*_]{1,2}/g, "$1") // **bold**/*italic* → content
    .length;
}

const BIO_MAX = 1000;

export function AffiliateEditDialog({ affiliate, open, onOpenChange, onUpdated, initialSection = "general", onSectionChange }: Props) {
  const form = useAffiliateForm(affiliate);
  const [activeSection, setActiveSection] = useState<SectionId>(initialSection);
  const [bioTab, setBioTab] = useState<"write" | "preview">("write");
  const bioRenderedLen = renderedLength(form.bio);

  useEffect(() => {
    if (open) setActiveSection(initialSection);
  }, [initialSection, open]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    form.setError("");
    form.setSubmitting(true);
    try {
      const record = await updateAffiliate({
        name: form.name,
        bio: form.bio || undefined,
        phone: form.phone || undefined,
        secondaryEmail: form.secondaryEmail || undefined,
        socials: buildSocialsPayload(form.socialState),
      });
      onUpdated(record);
      onOpenChange(false);
    } catch (err) {
      form.setError(err instanceof Error ? err.message : "Failed to save changes");
    } finally {
      form.setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-[900px] h-[100dvh] max-h-[100dvh] sm:h-[650px] sm:max-h-[90dvh] rounded-none border-0 sm:rounded-lg sm:border p-0 gap-0" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} showCloseButton={false}>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row h-full overflow-hidden">
          {/* Mobile horizontal tabs */}
          <div className="flex sm:hidden overflow-x-auto border-b gap-1 p-2">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => { setActiveSection(section.id); onSectionChange?.(section.id); }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                    activeSection === section.id
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {section.label}
                </button>
              );
            })}
          </div>

          {/* Desktop sidebar */}
          <div className="hidden sm:flex sm:flex-col w-48 shrink-0 border-r bg-muted/20 p-4 space-y-1">
            <h2 className="font-semibold mb-4 px-3">Manage Account</h2>
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => { setActiveSection(section.id); onSectionChange?.(section.id); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    activeSection === section.id
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {section.label}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              {form.error && (
                <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive mb-5">
                  {form.error}
                </div>
              )}

              {activeSection === "general" && (
                <div className="flex flex-col gap-6">
                  {/* === Public Information === */}
                  <div className="flex flex-col gap-4">
                    <div>
                      <h3 className="text-sm font-medium">Public Information</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Visible to anyone with your signup link.
                      </p>
                    </div>

                    {/* Photo + Name row */}
                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={() => form.fileRef.current?.click()}
                        className="relative size-24 shrink-0 rounded-full bg-muted flex items-center justify-center overflow-hidden border-2 border-border hover:border-foreground/30 transition-colors"
                      >
                        {form.photoPreview ? (
                          <img src={form.photoPreview} alt="Preview" className="size-full object-cover" />
                        ) : (
                          <Camera className="size-6 text-muted-foreground" />
                        )}
                        {form.uploading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-background/70">
                            <div className="size-4 animate-spin rounded-full border-2 border-muted-foreground border-t-foreground" />
                          </div>
                        )}
                      </button>
                      <input ref={form.fileRef} type="file" accept="image/*" onChange={form.handlePhotoSelect} className="hidden" />
                      <div className="flex flex-col gap-1.5 flex-1">
                        <Label htmlFor="edit-name">Name *</Label>
                        <Input id="edit-name" value={form.name} onChange={(e) => form.setName(e.target.value)} required />
                      </div>
                    </div>

                    {/* Bio */}
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between">
                        <Label>Bio</Label>
                        <span className={`text-xs ${bioRenderedLen > BIO_MAX ? "text-destructive" : "text-muted-foreground"}`}>{bioRenderedLen}/{BIO_MAX}</span>
                      </div>
                      <div className="rounded-md border border-input overflow-hidden">
                        <div className="flex border-b border-input">
                          <button
                            type="button"
                            onClick={() => setBioTab("write")}
                            className={`px-3 py-1.5 text-xs font-medium transition-colors ${bioTab === "write" ? "bg-background text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                          >
                            Markdown
                          </button>
                          <button
                            type="button"
                            onClick={() => setBioTab("preview")}
                            className={`px-3 py-1.5 text-xs font-medium transition-colors ${bioTab === "preview" ? "bg-background text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                          >
                            Preview
                          </button>
                        </div>
                        {bioTab === "write" ? (
                          <textarea
                            id="edit-bio"
                            value={form.bio}
                            onChange={(e) => form.setBio(e.target.value)}
                            rows={4}
                            placeholder="A short bio about yourself. Supports **bold**, *italic*, and [links](url)."
                            className="w-full bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none resize-none"
                          />
                        ) : (
                          <div className="px-3 py-2 text-sm min-h-[4.5rem] prose prose-sm prose-invert max-w-none [&_a]:text-primary [&_a]:underline">
                            {form.bio ? (
                              <Markdown allowedElements={["p", "strong", "em", "a"]} unwrapDisallowed>
                                {form.bio}
                              </Markdown>
                            ) : (
                              <span className="text-muted-foreground">Nothing to preview</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Social media */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {SOCIAL_PLATFORMS.map((platform) => {
                        const state = form.socialState[platform.key];
                        const Icon = platform.icon;
                        return (
                          <div key={platform.key} className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                              <Icon className="size-4" />
                            </span>
                            <Input
                              value={state.value}
                              onChange={(e) => state.set(e.target.value)}
                              placeholder={platform.placeholder}
                              className="pl-9"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                </div>
              )}

              {activeSection === "contact" && (
                <div className="flex flex-col gap-4">
                  <div>
                    <h3 className="text-sm font-medium">Contact Details</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      For internal use only. Not shared publicly.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Account email (readonly) */}
                    <div className="flex flex-col gap-1.5">
                      <Label>Account Email</Label>
                      <div className="relative">
                        <Input
                          value={affiliate.email}
                          readOnly
                          className="pr-9 bg-muted/30 text-muted-foreground cursor-default"
                        />
                        <Lock className="absolute right-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                      </div>
                    </div>

                    {/* Secondary Email */}
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="edit-secondary-email">Secondary Email</Label>
                      <Input id="edit-secondary-email" type="email" value={form.secondaryEmail} onChange={(e) => form.setSecondaryEmail(e.target.value)} placeholder="alternate@example.com" />
                    </div>
                  </div>

                  {/* WhatsApp */}
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="edit-phone">WhatsApp</Label>
                    <PhoneInput
                      id="edit-phone"
                      value={form.phone}
                      onChange={(v) => form.setPhone(v ?? "")}
                      defaultCountry="BR"
                      international
                      className="phone-input-wrapper"
                    />
                  </div>
                </div>
              )}

              {activeSection === "bank" && (
                <div className="flex flex-col items-center justify-center h-full text-center gap-3">
                  <Landmark className="size-10 text-muted-foreground/50" />
                  <div>
                    <h3 className="text-sm font-medium">Bank Details</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Payment information coming soon. You'll be able to add your bank account or PIX key for commission payouts.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t p-4 flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={form.submitting}>
                Discard
              </Button>
              <Button type="submit" className="flex-1" disabled={form.submitting || form.uploading || bioRenderedLen > BIO_MAX}>
                {form.submitting ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
